<template>
  <div class="max-w-md mx-auto">
     <form @submit.prevent="handleSubmit" class="flex flex-col gap-5">
        <div>
          <label for="name" class="block mb-1 font-medium text-gray-700">Name <span class="text-red-500">*</span></label>
          <InputText
            v-model="form.name"
            id="name"
            required
            class="w-full"
            :class="{'p-invalid': form.name.trim() === '' && isSubmitting}"
            placeholder="Enter item name"
          />
        </div>
        <div>
          <label for="description" class="block mb-1 font-medium text-gray-700">Description</label>
          <InputText
            v-model="form.description"
            id="description"
            class="w-full"
            placeholder="Optional description"
          />
        </div>
        <div>
          <label for="default_amount" class="block mb-1 font-medium text-gray-700">Default Amount <span class="text-red-500">*</span></label>
          <InputNumber
            v-model="form.default_amount"
            id="default_amount"
            mode="decimal"
            required
            class="w-full"
            :class="{'p-invalid': form.default_amount <= 0 && isSubmitting}"
            placeholder="0.00"
            :min="0"
          />
        </div>
        <Divider />
        <div class="flex justify-end gap-3">
          <Button size="small"
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            @click="emit('close')"
            class="p-button-outlined"
            type="button"
          />
          <Button size="small"
            label="Create"
            icon="fa fa-save"
            type="submit"
            :disabled="isSubmitting || !isFormValid"
            :loading="isSubmitting"
          />
        </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import type { CreateBillItemInput, BillItem } from '@/types/composition-functions';
import { useToastNotification } from '@/composables/useToastNotification';
import useVuelidate from '@vuelidate/core';
import { required, minValue } from '@vuelidate/validators';
import { useCreateBillItems } from '@/composables/mutations/useCreateBillItems';

const form = ref<{ name: string; description: string; default_amount: number }>({
  name: '',
  description: '',
  default_amount: 0,
});

const rules = {
  name: { required },
  default_amount: { required, minValue: minValue(0.01) },
  description: {
    dummy: () => true, // Placeholder for future validation if needed
  },
};

const v$ = useVuelidate(rules, form);

const emit = defineEmits(['close']);

const { createBillItem, isSubmitting } = useCreateBillItems();

const isFormValid = computed(() => !v$.value.$invalid);

const handleSubmit = async() => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useToastNotification({ message: 'Please fill in all required fields.', isSuccess: false });
    return;
  }

  try {
    const { data: createBillItemResponse } = (await createBillItem({ input: form.value }))!;
    if (createBillItemResponse?.createBillItem) {
      useToastNotification({ message: 'Bill item created successfully!' });
      form.value = { name: '', description: '', default_amount: 0 };
      v$.value.$reset();
      emit('close');
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'An error occurred.';
    useToastNotification({ message, isSuccess: false });
  }
};
</script>
